import { useState, useEffect, useContext } from 'react'
import { createClient } from 'graphqurl'
import { Modal, Button, Form, Row, Col, FormControl } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faExclamationCircle, faCircleQuestion } from '@fortawesome/free-solid-svg-icons'
import styles from '../styles/ApplyJob.module.scss'
import UserContext from '../UserContext'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'

const query = `
query ($id: Int!) {
  JobQuestion(where: {Job: {id: {_eq: $id}}, deletedAt: {_is_null: true}}) {
    id
    Question {
      questionType
      id
      question
      maxAnswer
      Answers {
        id
        answer
      }
    }
  }
}
`

export const SuccessApplyModal = ({ show, setShowModal, data, eventId }) => {
	const { t } = useTranslation()
	const { id, companyName, jobTitle } = data
	const [countDown, setCountDown] = useState(5)
	const [showAlertModal, setShowAlertModal] = useState(false)
	const [jobQuestion, setJobQuestion] = useState([])

	const router = useRouter()
	const locale = router.locale
	const pathName = router.pathname

	const client = createClient({
		endpoint: process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT as string
	})

	const closeModal = (showModal) => {
		if (pathName === '/browse-jobs') {
			showModal(false)
		}else if(eventId){
			window.location.href = `/${locale}/event/${eventId}`
		} else {
			window.location.href = `/${locale}/browse-jobs`
		}
	}

	const checkQuestion = async () => {
		setCountDown(5)
		const hasuraRes = await client.query({
			query: query,
			variables: { id: id }
		})

		const { data } = hasuraRes
		
		if (data.JobQuestion.length > 0) {
			setJobQuestion(data.JobQuestion)
			setShowModal(false)
			setShowAlertModal(true)
		} else {
			closeModal(setShowModal)
		}
	}

	useEffect(() => {
		if (show) {
			if (countDown > 0) {
				setTimeout(() => {
					setCountDown(countDown - 1)
				}, 1000)
			} else {
				checkQuestion()
			}
		}
	})
	
	return (
		<>
			<Modal
				show={show}
				size='lg'
				onHide={checkQuestion}
				centered
			>
				<Modal.Header closeButton className='border-0' />
				<Modal.Body className='text-center p-5 pt-0'>
					<FontAwesomeIcon icon={faCheckCircle} className={styles.iconSuccess} />
					<div className='mb-4'>
						<strong className={styles.textBold}>{t('apply_jobs_new.congratulations')}</strong>
						<p className={styles.textLight}>{t('apply_jobs_new.success_apply')}</p>
						<p className={styles.jobTitle}>{jobTitle} - {companyName}</p>
					</div>
					<p className={styles.textLight}>{t('apply_jobs_new.redirect')}{countDown}{t('apply_jobs_new.seconds')}</p>
				</Modal.Body>
			</Modal>
			<AlertModal show={showAlertModal} setShowModal={setShowAlertModal} closeModal={closeModal} data={data} jobQuestion={jobQuestion} />
		</>
	) 
}

const AlertModal = ({ show, setShowModal, closeModal, data, jobQuestion }) => {
	const { t } = useTranslation()
	const [showQuestionModal, setShowQuestionModal] = useState(false)

	const openQuestion = () => {
		setShowModal(false)
		setShowQuestionModal(true)
	}

	return (
		<>
			<Modal
				show={show}
				onHide={() => closeModal(setShowModal)}
				size='lg'
				centered
			>
				<Modal.Header closeButton className='border-0' />
				<Modal.Body className='text-center p-5 pt-0'>
					<FontAwesomeIcon icon={faExclamationCircle} className={styles.iconAlert} />
					<div className='mb-4'>
						<strong className={styles.textBold}>{t('apply_jobs_new.employer_will_ask_question')}</strong>
					</div>
					<Button variant='primary' className='text-white' onClick={openQuestion}>{t('apply_jobs_new.proceed_to_answer')}</Button>
				</Modal.Body>
			</Modal>
			<QuestionModal show={showQuestionModal} setShowModal={setShowQuestionModal} closeModal={closeModal} data={data} jobQuestion={jobQuestion} />
		</>
	)
}

const QuestionModal = ({ show, setShowModal, data, closeModal, jobQuestion }) => {
	const { t } = useTranslation()
	const { user }: any = useContext(UserContext)
	const [currentQuestion, setCurrentQuestion] = useState(0)
	const [showSuccessModal, setShowSuccessModal] = useState(false)
  
	const [yesnoAnswer, setYesnoAnswer] = useState(0)
	const [openendedAnswer, setOpenendedAnswer] = useState('')
	const [mcqAnswer, setMcqAnswer]: any[] = useState([])
  
	const isQuestion = jobQuestion.length !== 0
	const answers = isQuestion && jobQuestion[currentQuestion].Question.Answers
	const isYESNO = isQuestion && jobQuestion[currentQuestion].Question.questionType === 'YESNO'
	const isOPENENDED = isQuestion && jobQuestion[currentQuestion].Question.questionType === 'OPENENDED'
	const isMCQ = isQuestion && jobQuestion[currentQuestion].Question.questionType === 'MCQ'

	let token = ''
	if (typeof window !== 'undefined') {
		token = window.localStorage.getItem('token') as string
	}
  
	const client = createClient({
		endpoint: process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT as string,
		headers: {
			authorization: token ? `Bearer ${token}` : ''
		}
	})

	const next = () => {
		let complete = false
		let obj: any = {}
		if(isYESNO) {
			complete = true
			obj.jobQuestion = jobQuestion[currentQuestion].id
			obj.applicant = user.id
			obj.answer = yesnoAnswer
			setYesnoAnswer(0)
		} else if (isOPENENDED) {
			complete = true
			obj.jobQuestion = jobQuestion[currentQuestion].id
			obj.applicant = user.id
			obj.answerOther = openendedAnswer
			setOpenendedAnswer('')
		} else if (isMCQ) {
			obj = []
			complete = true
			for (let i = 0; i < mcqAnswer.length; i++) {
				const tempObj = {
					jobQuestion: jobQuestion[currentQuestion].id,
					applicant: user.id,
					answer: mcqAnswer[i]
				}
				obj.push(tempObj)
			}
			setMcqAnswer([]) 
		}

		if(complete) {
			client.query({
				query: `
        mutation($obj: [ApplicantJobQuestionAnswer_insert_input!]!) {
          insert_ApplicantJobQuestionAnswer(objects: $obj) {
            affected_rows
          }
        }
        `,
				variables: {
					obj: obj
				}
			}).then(() => {
				if (currentQuestion + 1 === jobQuestion.length) {
					setShowModal(false)
					setShowSuccessModal(true)
				} else {
					setCurrentQuestion(currentQuestion + 1)
				}
			})
		}
    
	}

	const onCheckBoxChange = (val: number): void => {
		if (mcqAnswer.includes(val)) {
			const temp = [...mcqAnswer]
			const index = temp.indexOf(val)
			if (index > -1) {
				temp.splice(index, 1)
			}
			setMcqAnswer(temp)
		} else if (mcqAnswer.length < jobQuestion[currentQuestion].Question.maxAnswer) {
			const temp = [...mcqAnswer]
			temp.push(val)
			setMcqAnswer(temp)
		}
	}

	const isButtonDisabled = () => {
		if(isYESNO && yesnoAnswer === 0) {
			return true
		} else if (isOPENENDED && openendedAnswer === '') {
			return true
		} else if (isMCQ && mcqAnswer.length === 0) {
			return true
		}
	}

	return (
		<>
			<Modal
				show={show}
				onHide={() => {
					closeModal(setShowModal)
				}}
				size='lg'
				centered
			>
				<Modal.Header closeButton className='border-0' />
				<Modal.Body className='text-center p-5 pt-0'>
					<FontAwesomeIcon icon={faCircleQuestion} className={styles.iconAlert} />
					{isQuestion && (
						<Col className={`${styles.answerColumn} my-0 mx-auto`}>
          	<strong className={styles.textBold}>{jobQuestion[currentQuestion].Question.question}</strong>
          	{isYESNO &&
              <Form className='text-center mt-4' style={{ width: 'max-content' }}>
              	{answers.map((ans, i) => {
              		return (
              			<Form.Check 
              				key={i}
              				className={`${i === 0 && 'mb-3'}`}
              				type='radio'
              				label={ans.answer}
              				name="answer"
              				id={ans.answer}
              				onChange={() => {
              					setYesnoAnswer(ans.id)
              				}}
              			>
              			</Form.Check>
              		)
              	})}
              </Form>
							}
							{isOPENENDED &&
              <FormControl
              	className='mt-4'
              	as='textarea'
              	rows={3}
              	placeholder='Answer the question here'
              	value={openendedAnswer}
              	onChange={(e) => {
              		setOpenendedAnswer(e.target.value)
              	}}
              />
							}
							{isMCQ &&
            <Form className='text-center mt-4' style={{ width: 'max-content' }}>
            	{answers.map((ans, i) => {
              	return (
              		<Form.Check
              			key={i}
              			className='mb-3'
              			type='checkbox'
              			label={ans.answer}
            				name="answer"
              			id={ans.answer}
            				checked={mcqAnswer.includes(ans.id)}
            				onChange={() => {
            					onCheckBoxChange(ans.id)
            				}}
              		>
              		</Form.Check>
              	)
            	})}
            </Form>
							}
						</Col>
					)}
				</Modal.Body>
				<Modal.Footer>
					{isQuestion &&
          <Row className='w-100 align-items-center'>
          	<Col xs={7} className='text-end p-0'>
          		<p className={`${styles.currentQuestion} m-0`}>{t('apply_jobs_new.questions')} {currentQuestion + 1} {t('apply_jobs_new.of')} {jobQuestion.length}</p>
          	</Col>
          	<Col className='text-end p-0'>
          		<Button 
          			variant='primary' 
          			className='text-white' 
          			onClick={next} 
          			disabled={isButtonDisabled()}
          		>
          			{currentQuestion + 1 === jobQuestion.length ? t('apply_jobs_new.finish') : t('apply_jobs_new.next')}
          		</Button>
          	</Col>
          </Row>
					}
				</Modal.Footer>
			</Modal>
			<SuccessAnswer show={showSuccessModal} setShowModal={setShowSuccessModal} closeModal={closeModal} data={data} />
		</>
	)
}

const SuccessAnswer = ({ show, setShowModal, closeModal, data }) => {
	const { companyName, jobTitle } = data
	const { t } = useTranslation()

	return (
		<Modal
			show={show}
			size='lg'
			onHide={() => 
				closeModal(setShowModal)
			}
			centered
		>
			<Modal.Header closeButton className='border-0' />
			<Modal.Body className='text-center p-5 pt-0'>
				<FontAwesomeIcon icon={faCheckCircle} className={styles.iconSuccess} />
				<div className='mb-4'>
					<strong className={styles.textBold}>{t('apply_jobs_new.thank_you_for_answer')}</strong>
					<p className={styles.textLight}>{t('apply_jobs_new.you_applied_for')}</p>
					<p className={styles.jobTitle}>{jobTitle} - {companyName}</p>
				</div>
				<p className={styles.textLight}>{t('apply_jobs_new.employer_will_contact')}</p>
				<p className={`${styles.textLight} mb-3`}>{t('apply_jobs_new.goodluck_for_your_application')}</p>
				<p className={`${styles.textLight} mb-4`}>{t('apply_jobs_new.browse_other_jobs')}</p>
				<Button variant='primary' className='text-white' onClick={() => closeModal(setShowModal)}>{t('apply_jobs_new.back_to_browse_jobs')}</Button>
			</Modal.Body>
		</Modal>
	) 
}